/*
 * THE RELATIONSHIP-AUTHORING WORKSPACE (#2351).
 *
 * Every colour, radius, space and shadow is a governed token. The dark header
 * consumes `--mode-bar-*` — the SAME inverse pair the customization mode bar
 * and the OnMarble launcher already take, brand-derived in
 * `packages/experience/src/tokens.ts` — so a Coccolare box gets Coccolare's
 * dark, not Moonbase's. The quieter line is `--mode-bar-muted`, the governed
 * secondary foreground #2347 added for exactly this line (#2406): it replaces
 * four local `color-mix` derivations of the same value, and it is proven
 * against every registered brand in `mode-bar.test.ts` where the local mixes
 * were proven against nothing.
 *
 * The one boundary this file owns is the STAGED width, and it owns it in two
 * places because a media query cannot read a TypeScript constant —
 * `RelationshipWorkspace.stage.test.ts` fails if the literal below and
 * `WORKSPACE_STAGED_MAX_PX` ever disagree. That is the mechanism UX-03
 * established for `AppShell.module.css` and `conformance/stylesheet.test.ts`
 * uses for the shared drawer.
 */

.panel {
  padding: 0;
  overflow: hidden;
  max-width: min(1180px, calc(100vw - 2 * var(--sp-4)));
  border-radius: var(--r-lg);
}

.shell {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  /* The dialog is tall but never taller than the viewport: the BODY scrolls,
     the header and the commit footer do not. A workspace whose primary action
     leaves the screen when the candidate list grows is the clipping #2351
     forbids.

     THE VIEWPORT MINUS THE OVERLAY'S OWN INSET. `Modal.module.css` centres with
     `padding-top: 14vh`, so the space a panel actually has is `100vh - 14vh`,
     less a margin so it does not sit flush on the bottom edge. Getting this
     wrong put the commit footer below the fold at three separate widths —
     960x480, 768x1024 and 320x720 — so the arithmetic is written once here and
     the breakpoints below inherit it rather than contradict it. */
  max-height: min(calc(100vh - 14vh - var(--sp-2)), 900px);
  background: var(--bg);
}

/* ── the dark header, and the graph-context chip ─────────────────────────── */

.head {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-3);
  padding: var(--sp-4) var(--sp-5);
  background: var(--mode-bar-bg);
  color: var(--mode-bar-fg);
  /* THE CAP IS AN INVERSE SCOPE (#2406), the shape `CustomizationModeBar` and
     `RecordShell`'s feature cap already use: redefine the roles the kit reads,
     so the kit controls on this ground — the stepper, the supporting caption,
     the close button — take the cap's ink without learning that a cap exists.
     `--brand-text` is the stepper's active numeral, which the page's brand
     text cannot carry on a dark ground; the focus ring follows the mode bar's. */
  --ink: var(--mode-bar-fg);
  --muted: var(--mode-bar-muted);
  --surface: var(--mode-bar-bg);
  --brand-text: var(--mode-bar-fg);
  --focus-ring: var(--mode-bar-fg);
}

.headText { flex: 1; min-width: 0; }

.title {
  margin: 0;
  font-size: var(--fs-xl);
  font-weight: 650;
  letter-spacing: -0.01em;
  color: var(--mode-bar-fg);
}

.sub {
  margin: var(--sp-1) 0 0;
  font-size: var(--fs-sm);
  color: var(--mode-bar-muted);
}

.context {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  margin: var(--sp-3) 0 0;
  padding: var(--sp-1) var(--sp-3);
  border-radius: var(--mat-r-pill);
  background: color-mix(in oklab, var(--mode-bar-fg) 10%, transparent);
  font-size: var(--fs-sm);
  color: var(--mode-bar-fg);
}

.contextSlash, .contextArrow { color: var(--mode-bar-muted); }
.contextTarget { color: var(--accent); font-weight: 600; }

/* ── WHAT IS LEFT AFTER THE BUTTON OWNS ITS OWN SHAPE (#2406) ─────────────
   All three of `.close`, `.inspect` and `.clear` are already
   `<Button variant=… size="sm">`, and each re-declared what `Button` draws:
   the centring, the border, the radius, the transparent ground, the hover
   fill — and a `:focus-visible` outline identical, property for property, to
   the one `global.css` puts on every focusable thing.

   What survives is only what the kit cannot know: it is a 32px square rather
   than a text button. Its ink and hover were written here too, as the cap's
   — the ink is the inverse scope's `--ink` now, and the hover never applied at
   all: `Button`'s `.ghost:hover:not(:disabled)` outranks a one-class
   `.close:hover`, so that rule was dead the day it was written (#2406). */
.close {
  flex: none;
  width: 32px;
  height: 32px;
}

/* ── the step indicator (staged face only) ──────────────────────────────────
   The kit's `Steps` in its compact presentation, and the caption beside it.
   What stays is where the pair sits in the cap. The three hand-drawn dots and
   their caption paint are gone (#2406). */

.steps { display: flex; align-items: center; gap: var(--sp-2); margin: var(--sp-2) 0 0; }

/* ── the per-row info affordance ─────────────────────────────────────────── */

.rowWrap { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; }
/* The cells are layout-transparent: the ROW owns the columns, so a cell is a
   wrapper whose only job is to be the grid cell the a11y tree needs. */
.rowCell { display: grid; min-width: 0; }

/* The outline and the ground this rule used to draw by hand ARE the kit's
   `secondary` variant, which the element now asks for — it was `ghost`, and
   the CSS painted a secondary over it. */
.inspect {
  /* 44px, not the 34px the mockup drew: ROOT's correction, and the floor every
     coarse-pointer control in this estate is held to. The GLYPH stays small —
     the target is the thing that has to be 44. */
  width: 44px;
  height: 44px;
  margin-left: var(--sp-1);
}

/* ── the preview sheet's own head ────────────────────────────────────────── */

.sheetHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  padding-bottom: var(--sp-2);
}
.sheetGrip {
  width: 36px;
  height: 4px;
  border-radius: var(--mat-r-pill);
  background: var(--border);
}

/* ── FORCED COLOURS: the inverse ground hands over to the user's palette ───
 *
 * The same block `CustomizationModeBar.module.css` carries for the same reason,
 * and the same one: this is the other surface in the estate that paints
 * `--mode-bar-*`. Without it the DECLARED colours stay brand-dark while the
 * BROWSER paints Canvas/CanvasText, so the two disagree — measured as three
 * serious axe color-contrast findings (#eaf1fb on #ffffff, 1.13) even though
 * the rendering itself was legible.
 */
@media (forced-colors: active) {
  .head {
    forced-color-adjust: auto;
    background: Canvas;
    color: CanvasText;
    border-bottom: 1px solid CanvasText;
  }
  .title, .sub, .context, .contextSlash, .contextArrow, .contextTarget, .close {
    color: CanvasText;
  }
  .context { background: Canvas; border: 1px solid CanvasText; }
  /* A SELECTED row's outline is `ListRow`'s own forced-colours arm. The
     highlight is this list's state, so its outline stays here. */
  .candidate[data-highlighted='yes'] { outline: 2px solid Highlight; outline-offset: -2px; }
  /* The rail mark's forced-colours arm is at the END of this sheet: its base
     rules come later than this block, and a conditional rule above an
     identical base selector never applies (cascade-order.test). */
  /* The state words are the kit `Pill`, whose transparent border is what
     forced colours draw, so they keep an edge without a rule here. */
}

/* ── the three panes ─────────────────────────────────────────────────────── */

.body {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr) 320px;
  min-height: 0;
  border-bottom: 1px solid var(--hair);
}

.rail {
  min-height: 0;
  overflow-y: auto;
  padding: var(--sp-3);
  border-right: 1px solid var(--hair);
  background: var(--surf-2);
}

/* THE KIT HAS THE LEADING-ICON PRIMITIVE NOW (#2406). The note that stood
   here said "`Input.module.css` has no leading-icon primitive, so there is
   nothing in the kit to adopt here" — true when it was written, and `SearchField`
   is the answer to it. Both searches pass through it; what is left is where
   this surface puts them. */
.railSearch { margin-bottom: var(--sp-3); }

/* `.railEmpty`, `.workEmpty`, `.previewUnavailable` and the paint of
   `.previewEmpty` are the kit's `EmptyState` now (#2406): six "nothing here"
   sentences in one dialog, drawn three ways, become one placeholder. */

.railGroup { margin-bottom: var(--sp-4); }

/* ONE MICRO-LABEL, THREE PLACES, and the only thing that differed was the
   margin. `FactList.module.css:63-77` records that five host sheets drifted to
   .04em, .07em and .08em with nobody choosing; this file is the .08em host,
   three times over. Reconciling to the kit's .04em/640 moves pixels, so it is
   NOT this change — this one just stops saying it three times. */
.railHead, .previewHead, .connected h4, .basketLabel {
  font-size: var(--fs-xs);
  font-weight: 650;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}
.railHead { margin: 0 0 var(--sp-2) var(--sp-2); }

.railList { display: grid; gap: var(--sp-0-5); }

/* THE RAIL'S ROWS ARE THE KIT'S `ListRow` (#2406). Its box, its 44px floor,
   its hover, its focus ring and the selected tint-and-edge this rule used to
   draw are the row's now — and one of them was a defect: the hover painted
   `--surf-2` on a rail whose ground IS `--surf-2`, so hovering a type drew
   nothing at all. `.railItem` survives only for the coarse-pointer height at
   the foot of this file. */

/* The rail's mark is the kit's `IconTile` (lg) — the entity-mark box four
   stylesheets drew before it existed, and this one was the fifth (#2406). */

/* The one-text-block-two-callers rule that stood here is `ListRow`'s text
   block now — a primary line over a truncating secondary, declared once in
   the kit rather than once per list (#2406). */

/* The rail's count is the kit's `CountPill` — `outline` at rest, `brand` on
   the chosen type. `CountPill.tsx` names `.railCount` as one of the seven
   copies it was extracted from; this was the last one standing (#2406). */

/* ── the candidate / create workspace ────────────────────────────────────── */

.work {
  display: flex;
  flex-direction: column;
  min-height: 0;
  /* The PANE scrolls, not the dialog. On a short screen the mode segment, the
     search box and the filters are fixed cost and the list is what is left; if
     that leaves too little, this is the scroller that keeps every control
     reachable instead of clipping the one a person is using. */
  overflow-y: auto;
  padding: var(--sp-4);
  gap: var(--sp-3);
}

.modes { display: flex; align-items: center; justify-content: space-between; gap: var(--sp-3); }
.tally { margin: 0; font-size: var(--fs-sm); color: var(--muted); font-variant-numeric: tabular-nums; }

.candidates { display: flex; flex-direction: column; min-height: 0; gap: var(--sp-3); }

/* The 44px floor is this surface's, from #2358's touch ruling; the rest is
   `SearchField`'s. */
.searchRow input { min-height: 44px; }

.filters { display: flex; align-items: center; gap: var(--sp-2); flex-wrap: wrap; }

/* The filter pills are the kit's `Chip`: it owns the shape, the on-state and
   the `aria-pressed` that carries it. The host owns only the coarse-pointer
   floor, in the `pointer: coarse` block below. Painting the on-state here
   would undo `Chip.module.css`'s own 1.4.1 decision — that the state reads
   through background and border rather than through text colour. */

.sort { margin-left: auto; min-width: 190px; }

.hint { margin: 0; font-size: var(--fs-xs); color: var(--muted); }

.list {
  display: grid;
  gap: var(--sp-0-5);
  /* Takes what the pane has left, and never collapses to nothing: at 960x480
     the list resolved to zero height and Playwright reported it HIDDEN — a
     candidate list with no candidates visible is not a smaller list, it is a
     broken one. ~two rows is the floor; below that the pane scrolls. */
  flex: 1 1 auto;
  min-height: 104px;
  overflow-y: auto;
}

/* THE CANDIDATE IS THE KIT'S `ListRow` (#2406) — box, text block, hover,
   focus ring, the selected tint-and-edge and the refused (`aria-disabled`)
   treatment. What stays is this list's own state: the HIGHLIGHT is the row
   the preview is following, which is not the same fact as being selected.
   `:not([data-selected])` so a highlighted row that is also in the basket
   keeps reading as chosen. */
.candidate[data-highlighted='yes'] { border-color: var(--border); }
.candidate[data-highlighted='yes']:not([data-selected]) { background: var(--surf-2); }

.rowMark {
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  border: 1.5px solid var(--border);
  border-radius: var(--r-sm);
  background: var(--bg);
  font-size: 13px;
  line-height: 1;
  color: var(--brand-contrast);
}
.rowMark[data-kind='radio'] { border-radius: 50%; }
.candidate[data-selected] .rowMark { background: var(--brand); border-color: var(--brand); }
.candidate[aria-disabled='true'] .rowMark { opacity: 0.5; }


/* `.state` and `.connectedChip` are the kit's `Pill` (#2406): the neutral
   tone for "Already linked" and a connection, `warn` for "Inactive" — whose
   ink-on-warn-fill-with-a-warn-edge was already exactly what `.state
   [data-tone='inactive']` hand-drew. The note that stood here kept them
   private because "adopting it moves pixels"; it does, by one type step, and
   the dialog now says a state the way every other surface does. The one thing
   Pill could not do was carry the `id` the row's `aria-describedby` names —
   it can now. */

.moreRow { display: flex; align-items: center; gap: var(--sp-3); }

.create { display: grid; gap: var(--sp-4); min-height: 0; overflow-y: auto; }

/* ── THE THREE NOTICES ARE THE KIT'S NOW (#2406) ───────────────────────────
   `Notice.module.css` opens by naming these very classes — "RelationshipWorkspace
   `.createNote` info", "`.review` warn", "`.notice` warn" — as three of the five
   host copies it was written to replace. It had never been adopted here,
   because `NoticeProps` carried no `ref` and this workspace FOCUSES its
   announcement so a keyboard reader lands on the refusal rather than hunting
   for it. The primitive takes a `ref` now, so the paint below goes.

   What is left of `.createNote` and `.review` is the geometry the notice does
   not own: the review's internal stack. The paint, the border, the radius, the
   tone pair and the focus ring are all the primitive's. */

.review { display: grid; gap: var(--sp-2); }
.review h4 { margin: 0; font-size: var(--fs-md); }
.review p { margin: 0; font-size: var(--fs-sm); color: var(--muted); }
.reviewList { display: grid; gap: var(--sp-2); }
.reviewItem {
  display: grid;
  gap: var(--sp-0-5);
  text-align: left;
  min-height: 44px;
  padding: var(--sp-2);
  border: 1px solid var(--hair);
  border-radius: var(--r-md);
  background: var(--bg);
}
.reviewItem span { font-size: var(--fs-xs); color: var(--muted); }

/* ── the preview ─────────────────────────────────────────────────────────── */

.preview {
  min-height: 0;
  overflow-y: auto;
  padding: var(--sp-4);
  border-left: 1px solid var(--hair);
  background: var(--surf-2);
}

.previewSheet { padding: var(--sp-4); }

.previewHead, .connected h4 { margin: 0 0 var(--sp-2); }

.previewBody { display: grid; gap: var(--sp-4); align-content: start; }

/* The kit's `EmptyState`, placed: the preview pane is tall, so the sentence
   sits in its middle rather than at its top. The paint is the kit's. */
.previewEmpty {
  display: grid;
  align-content: center;
  height: 100%;
  min-height: 180px;
}

.connected { display: grid; gap: var(--sp-2); }
.connected p { margin: 0; font-size: var(--fs-sm); color: var(--muted); }
.connectedChips { display: flex; flex-wrap: wrap; gap: var(--sp-2); }

.previewActions { display: flex; align-items: center; gap: var(--sp-3); flex-wrap: wrap; }
.openLink { font-size: var(--fs-sm); color: var(--brand-text); }

/* ── the basket and the commit footer ────────────────────────────────────── */

.foot {
  display: grid;
  gap: var(--sp-3);
  padding: var(--sp-3) var(--sp-5) var(--sp-4);
  background: var(--bg);
}

.basket {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  flex-wrap: wrap;
  min-height: 34px;
}


.basketEmpty { font-size: var(--fs-sm); color: var(--muted); }

/* ── THE BASKET CHIP IS THE KIT'S, WHOLE (#2406) ───────────────────────────
   This was a host chip drawn AROUND `FilterChip` — its own inline-flex,
   padding, hairline, pill radius, ground and type — plus a re-style of
   FilterChip's own remove control into a 22px round button. Two chips for one
   value, and the outer one existed only because `FilterChip` took no
   `className` and forwarded no props, so a test id needed a wrapper and a
   wrapper needed a look.

   `FilterChip` takes both now, and carries its own coarse-pointer touch floor,
   which this host rule was the only thing supplying. `.chip` survives as the
   name the staged-review layout reaches for, with nothing painted on it. */

/* `ghost` already draws the borderless transparent button at `sm`'s height
   and type. The accent colour and the shove to the right are this surface's. */
.clear {
  margin-left: auto;
  color: var(--brand-text);
}


.actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: var(--sp-4);
  padding-top: var(--sp-3);
  border-top: 1px solid var(--hair);
}

.edge { min-width: 0; }
.buttons { display: flex; align-items: center; gap: var(--sp-2); justify-content: flex-end; }

.confirm { display: grid; gap: var(--sp-3); padding: var(--sp-4); }
.confirm h3 { margin: 0; font-size: var(--fs-lg); }
.confirm p { margin: 0; color: var(--muted); font-size: var(--fs-sm); }
.confirmActions { display: flex; justify-content: flex-end; gap: var(--sp-2); }

/* The visually-hidden recipe, in its modern form: `clip-path: inset(50%)`
   needs no negative offset, which is the only reason the classic version
   carries `margin: -1px`. Same clip the kit's own `Modal.module.css` uses for
   its accessible name. */
.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

/* ── TABLET: the preview is a sheet, so the workspace keeps two panes ─────── */

@media (max-width: 860px) {
  .body { grid-template-columns: 240px minmax(0, 1fr); }
  /* NO taller cap here. This used to raise it to 92vh to use the room the
     absent preview pane leaves, and on a 768x1024 tablet that put the commit
     footer 61px below the fold — the overlay's 14vh sits on top of whatever the
     panel asks for, so 92 + 14 does not fit in 100. The base rule's formula is
     the space there is. */
  .actions { grid-template-columns: minmax(0, 1fr); }
  .buttons { justify-content: stretch; }
  .buttons > * { flex: 1; }
}

/* ── COARSE POINTER: the 44px floor, everywhere a finger lands ─────────────
 *
 * ROOT's corrections to #2358's own measurements: the info target is 44 rather
 * than 34, metadata controls are 44 rather than 42, and rows are 54. The rest
 * of this block is the same floor applied to controls the mockups did not
 * measure — the chip removes (22px), the filter toggles (36px) and the header
 * close (32px) are all finger targets on a touch device.
 *
 * A MEDIA query on the pointer, not on the width: a touch laptop at 1440px
 * needs the floor and a mouse at 390px does not, and the estate's other coarse
 * rules (`Connections`, `RelationshipGraph`) draw the line in the same place.
 */
@media (pointer: coarse) {
  .candidate { min-height: 54px; }
  .railItem { min-height: 54px; }
  .toggle { min-height: 44px; }
  .clear { min-height: 44px; padding: 0 var(--sp-3); }
  .close { width: 44px; height: 44px; }
  /* The chip's floor and its remove control's are `Chip.module.css`'s now. */
  .searchRow input, .railSearch input { min-height: 44px; }
  /* The edge facts are `Form` controls, so the floor is applied through the
     shared control height rather than to each field: one variable, and the
     kit's own inputs follow it. */
  .edge { --ctl-h: 44px; }
  .reviewItem { min-height: 54px; }
}

/* ── SHORT VIEWPORTS: less chrome, never fewer panes ───────────────────────
 *
 * Measured at 960x480 (the automotive profile in `record-relations.spec.ts` and
 * `cache-convergence.spec.ts`): 86vh is 413px there, and a header plus a
 * commit footer can eat all of it, leaving the `minmax(0, 1fr)` body at zero
 * and the candidate list hidden behind the footer.
 *
 * Two attempts got this wrong before it was measured, and both are worth
 * recording. Setting `.shell { max-height: none }` so the PANEL could scroll
 * produced a 1255px panel in a 480px viewport with its primary action at
 * y=1266 — because `Modal`'s `className` prop REPLACES its own panel class, so
 * this panel never carried Modal's 84vh cap and `.shell` is the only thing
 * holding the dialog inside the screen. Capping the panel at 96vh instead put
 * its footer 40px below the fold, because the centred overlay adds 14vh of top
 * padding.
 *
 * So the cap stays on the shell and the three-row layout stays: the rail, the
 * workspace and the preview already scroll inside it. What a short screen gives
 * up is CHROME — a tighter header, no restating subtitle — which is affordable
 * only because the edge facts now render as a row rather than stacked, taking
 * the footer from ~250px to ~110px.
 */
@media (max-height: 560px) {
  /* 84vh on a short screen — the base formula with a larger margin, because a
     480px-tall viewport has no room to spare: measured at 960x480, 86vh plus
     the overlay's 14vh reached past the fold and 94vh put the primary action's
     lower half below it (`commitRect.bottom 506` against `innerHeight 480`). */
  .shell { max-height: 84vh; }
  .head { padding: var(--sp-2) var(--sp-4); }
  .title { font-size: var(--fs-lg); }
  .context { margin-top: var(--sp-2); }
  /* The one line a short screen can afford to lose: it explains the dialog a
     person has already opened, and the rail says the rest. */
  .sub { display: none; }
  .work { padding: var(--sp-3); }
  .foot { padding: var(--sp-2) var(--sp-4) var(--sp-3); }
  .actions { padding-top: var(--sp-2); }
}

/* ── PHONE: one stage at a time ──────────────────────────────────────────────
 *
 * 600px, and the same literal is `WORKSPACE_STAGED_MAX_PX` in the component —
 * `RelationshipWorkspace.stage.test.ts` fails if they drift. The stage is a
 * data attribute rather than a class so the component's state and the layout
 * cannot disagree about which region is on screen.
 */

@media (max-width: 600px) {
  /* `dvh`, not `vh`: the DYNAMIC viewport shrinks when the virtual keyboard
     opens, which is what keeps the primary action and the search box above it
     rather than under it (ROOT's keyboard correction). `vh` is the LARGE
     viewport and does not move, which is how a phone dialog ends up with its
     commit button behind the keyboard. */
  .panel { max-width: 100vw; border-radius: 0; }
  /* THE VIEWPORT MINUS THE OVERLAY'S OWN INSET. `Modal.module.css` centres with
     `padding-top: 14vh`, so a panel capped at the full `100dvh` starts 14% down
     and ends 14% below the bottom — measured at 320x720 as a panel at y=100
     with a height of 720, whose commit button was 100px past the fold and could
     not be clicked at all. `dvh` for the cap so the keyboard shrinks it; `vh`
     for the inset because that is the unit the overlay itself uses. */
  .shell { max-height: calc(100dvh - 14vh); }
  .body { grid-template-columns: minmax(0, 1fr); }
  .rail, .work { border-right: 0; }
  /* The subtitle explains a dialog the person has just opened, and on a phone
     it costs two lines of the list. #2358's mockups carry the record chip and
     the step indicator here and no sentence. */
  .sub { display: none; }
  /* The count rides on the PRIMARY (`Review 2`) and the selections are step
     3's own content, so the strip is not drawn before it. One less row, and one
     less place for a count to disagree with itself. */
  .shell[data-stage='type'] .basket,
  .shell[data-stage='target'] .basket { display: none; }

  /* ONE region per step. The rules are keyed on the shell's `data-stage`, which
     the component sets from the NORMALISED stage — so a state that could not
     be on this step cannot be drawn on it either. */
  .shell[data-stage='type'] .work { display: none; }
  .shell[data-stage='target'] .rail,
  .shell[data-stage='review'] .rail { display: none; }
  .shell[data-stage='review'] .candidates { display: none; }
  /* The edge facts belong to the REVIEW step: asking for a purpose while
     somebody is still choosing records is the three-pane layout pretending to
     be a phone. */
  .shell[data-stage='type'] .edge,
  .shell[data-stage='target'] .edge { display: none; }
  /* …and the basket is the review step's own content, listed with a remove per
     row (#2358 step 3) rather than a chip strip above the fold. */
  .shell[data-stage='review'] .basket { display: grid; gap: var(--sp-2); }
  .shell[data-stage='review'] .chip { justify-content: space-between; }

  .head { padding: var(--sp-3) var(--sp-4); }

  /* THE THUMB BAR. Sticky to the bottom of the dialog, consuming the device's
     own safe-area inset — the tokenised system #2358 P8 asks for rather than a
     per-surface patch — and Back on the left, away from the sweep. */
  .foot {
    position: sticky;
    bottom: 0;
    padding: var(--sp-3) var(--sp-4) max(var(--sp-4), env(safe-area-inset-bottom));
    box-shadow: 0 -1px 0 var(--hair);
    background: var(--bg);
  }
  .buttons { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: var(--sp-3); }
  .buttons > * { min-height: 50px; }
  /* SEARCH IMMEDIATELY ABOVE THE PRIMARY, and only on the records step: the
     candidates pane is a column, so ordering the search row last puts it at the
     bottom of that column, which is the bottom of the screen. */
  .candidates { display: flex; flex-direction: column; }
  .searchRow { order: 3; }
  .filters { order: 2; }
  .list { order: 1; flex: 1 1 auto; }
  .moreRow { order: 4; }
}

/* ── 320px / 200% zoom: the same flow, nothing overlapping ─────────────────
 *
 * #2358's acceptance asks for the 320px equivalent explicitly. Nothing here is
 * a new layout — it is the staged face with the two-column bits allowed to
 * stack, because at 320 CSS pixels a label and its control cannot share a row.
 */
@media (max-width: 380px) {
  .filters { gap: var(--sp-1); }
  .sort { min-width: 0; margin-left: 0; }
  .buttons { grid-template-columns: minmax(0, 1fr); }
  .buttons > * { width: 100%; }
}

/* The rail mark in forced colours — BELOW its base rules on purpose, or the
   base `.candidate[data-selected] .rowMark` background wins and a selected
   row's mark is not drawn in Highlight at all. */
@media (forced-colors: active) {
  .rowMark { border-color: CanvasText; }
  .candidate[data-selected] .rowMark { background: Highlight; color: Canvas; }
}
